'use client'

import { useEffect, useRef } from 'react'

import { styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Colors } from '@b-stock/fe-core/design-system'
import type { AutocompleteSuggestion } from '@b-stock/search-api-client'

import {
  SearchDropdownWrapper,
  SearchDropdownContainer,
  SearchDropdownItem,
} from './ui'

interface SearchAutocompleteProps {
  suggestions: AutocompleteSuggestion[]
  isOpen: boolean
  onSelectSuggestion: (suggestion: string) => void
  onClose: () => void
  selectedIndex: number
  searchQuery: string
  isFetching?: boolean
  autocompleteQueryValue?: string
}

const SearchTitle = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  font-weight: 600;
  color: ${Colors.Semantic.Neutral.black};
  background: ${Colors.Semantic.Neutral.white};
  border: none;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${Colors.Semantic.Neutral.light_grey};
  }
`

const AutocompleteSuggestionItem = styled(SearchDropdownItem)`
  padding: 0.75rem 1.5rem 0.75rem 2rem;
`

const NoResults = styled.div`
  padding: 0.75rem 1.5rem 1.25rem 1.5rem;
  color: ${Colors.Semantic.Neutral.dark_grey};
  text-align: center;
`

export default function SearchAutocomplete({
  suggestions,
  isOpen,
  onSelectSuggestion,
  onClose,
  selectedIndex,
  searchQuery,
  isFetching = false,
  autocompleteQueryValue = '',
}: SearchAutocompleteProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const selectedItemRef = useRef<HTMLButtonElement>(null)

  // Scroll selected item into view when keyboard navigation changes
  useEffect(() => {
    if (selectedItemRef.current && selectedIndex >= 0) {
      selectedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
    }
  }, [selectedIndex])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <SearchDropdownWrapper $isOpen={isOpen}>
      <SearchDropdownContainer
        ref={containerRef}
        role="listbox"
        aria-label="Search suggestions"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <SearchTitle
          onClick={() => onSelectSuggestion(searchQuery)}
          onMouseDown={(e) => e.preventDefault()}
        >
          <FormattedMessage
            id="Search.autocomplete.searchFor"
            values={{ query: searchQuery }}
          />
        </SearchTitle>
        {suggestions.length > 0
          ? suggestions.map((item, index) => (
              <AutocompleteSuggestionItem
                key={item.suggestion}
                ref={index === selectedIndex ? selectedItemRef : null}
                $isSelected={index === selectedIndex}
                onClick={() => onSelectSuggestion(item.suggestion)}
                onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                role="option"
                aria-selected={index === selectedIndex}
                data-testid={`autocomplete-suggestion-${index}`}
              >
                {item.suggestion}
              </AutocompleteSuggestionItem>
            ))
          : !isFetching &&
            autocompleteQueryValue.length >= 3 && (
              <NoResults>
                <FormattedMessage id="Search.autocomplete.noSuggestions" />
              </NoResults>
            )}
      </SearchDropdownContainer>
    </SearchDropdownWrapper>
  )
}
